import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { Heading } from '@uk-source-web/heading';
import { Paragraph } from '@uk-source-web/paragraph';
import { deleteBasket, useAppDispatch, useAppSelector, UserBasketItem } from '@/store';
import { SWBasketProps } from './SWBasket.types';

import { useModalContext } from '@/hooks/modal/useModal';
import {
  CreditVettingPayload,
  ProductOrderBasketProduct,
  creditVettingProductOrder,
  productOrder,
} from '@/services/product.services';

import BasketItem from './BasketItem';

import { PRODUCT_BUNDLE_TYPENAME, transformToItemOrder } from './SWBasket.helper';

import SWBasketStyles, {
  BasketButtonsInner,
  ContainerHeaderStyles,
  EmptyBasket,
  SWBasketContainer,
  SWBasketHeader,
  SWBasketSummary,
  SWBasketSummaryInner,
  WrapperBasketButtons,
} from './SWBasket.styles';
import handleTranslation from '@/helpers/handleTranslation';
import { BasketHorizontalStepper } from './BasketHorizontalStepper/BasketHorizontalStepper';

import usingOpcoUrl from '@/helpers/prefixHelper';
import BasketDiscount from './BasketDiscount/BasketDiscount';
import BasketStrip from './BasketStrip/BasketStrip';
import { checkForZeroOrNull } from '@/helpers/nullOrZero';
import { Button } from '@uk-source-web/button';
import { OPCOOptions } from '@/types/Services.types';
import { CreditVettingForm, CreditVettingFormRef } from '../CreditVettingForm/CreditVettingForm';

import { useBasketSummary } from '@/contexts/BasketSummaryContext/BasketSummary';
//tenant context
import { useValid } from '@/contexts/TenantContext/TenantContext';
import axios, { AxiosResponse } from 'axios';
import { LoadingWrapper } from '../../Elements/SWPricingCard/SWPricingCard.styles';
import { LoadingSpinner } from '@legacy-vfuk/core-loading-spinner';

const OPCO = process.env.GATSBY_OPCO;

interface MicrosoftRequirementsSummary {
  needsMCA?: boolean;
}

const SWBasket = ({
  name,
  continueShoppingButton,
  requestCallbackButton,
  goToCheckoutButton,
  cvInlineNotificationMessage,
}: SWBasketProps) => {
  const { width } = useWindowSize();
  const { toggleModal, setErrorInfo } = useModalContext();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(({ userSession }) => userSession);
  const { userBasket } = useAppSelector(({ userBasket: { data } }) => ({
    userBasket: data,
  }));
  const { tenantPortingRequired } = useAppSelector(({ userBasket: { tenantPortingRequired } }) => ({
    tenantPortingRequired,
  }));
  const [inlineNotificationVisible, setInlineNotificationVisible] = useState(false);

  const yourOrderSummary = handleTranslation('Your Order Summary', 'Carrinho de compras');
  const emptyBasket = handleTranslation('Your basket is empty', 'O seu carrinho está vazio');
  const emptyBasketSubtitle = handleTranslation(
    'The items you add will appear here',
    'Os itens que você adicionar aparecerão aqui'
  );
  const accountTrans = handleTranslation('Account', 'Conta');

  const [currentStep, setCurrentStep] = useState(1);

  const [disableContinueButton, setDisableContinueButton] = useState<boolean>(true);

  //for microsoft products
  const [microsoftAdditionalData, setMicrosoftAdditionalData] = useState<string>(() => {
    return localStorage.getItem('microsoftAdditionalData') || 'warn';
  });

  const setMicrosoftAdditionalDataState = (data: string) => {
    setMicrosoftAdditionalData(data);
  };

  const { basketSummary, fetchBasketSummary } = useBasketSummary();
  const basketSummaryData = basketSummary?.data as MicrosoftRequirementsSummary | undefined;
  const isSummaryLoaded = Boolean(basketSummaryData);
  const needsMCA = basketSummaryData?.needsMCA ?? false;
  const requiresAnyMicrosoftStep = needsMCA ;

  //determine the initial state of continue purchase button and enable it even after quantity
  useEffect(() => {
    const hasMicrosoftProduct = userBasket.items.some(item => item.msftProduct);
    const hasAdditionalInfoRequirement = basketSummary?.data?.item?.some(item => item.needsAdditionalInformation);

    if (hasMicrosoftProduct && (requiresAnyMicrosoftStep || hasAdditionalInfoRequirement) && microsoftAdditionalData !== 'success') {
      setDisableContinueButton(true);
    } else {
      setDisableContinueButton(false);
    }
  }, [userBasket.items, microsoftAdditionalData, requiresAnyMicrosoftStep, basketSummary]);

  const montlyCostValue = checkForZeroOrNull(userBasket.total?.monthly?.gross, 'NA');
  const upfrontCostValue = checkForZeroOrNull(userBasket.total?.upfront?.gross, 'NA');

  // analytics logic
  let itemsTracking = userBasket.items.map(item => {
    return {
      cartProductName: item.name,
      cartItemId: item.id,
      cartProductCategory: item.category,
      cartProductQuantity: item.quantity,
      cartProductPrice: item.dxlInformation?.price[0].price,
      cartUpfrontTotal: upfrontCostValue,
      cartMonthlyTotal: montlyCostValue,
    };
  });

  useEffect(() => {
    window.utag?.link({ itemsTracking });
  }, [userBasket.items]);

  useEffect(() => {
    if (microsoftAdditionalData == 'warn') {
      window.utag?.view({
        event_name: 'ui_interaction',
        component_name: 'Click Component',
        event_category: 'Basket',
        event_label: 'Edit',
        event_action: 'Additional Information',
        compoennt_type: 'Button',
      });
    }
  }, [microsoftAdditionalData]);

  const creditVettingFormRef = useRef<CreditVettingFormRef>(null);

  const handleContinueClick = () => {
    if (creditVettingFormRef.current) {
      creditVettingFormRef.current.submit();
    }
  };

  //tenant context
  const {
    tenantName,
    setTenantName,
    isTenantValid,
    setIsTenantValid,
    principleName,
    setPrincipleName,
    isPrincipleNameValid,
    setIsPrincipleNameValid,
    email,
    setEmail,
    isEmailValid,
    setEmailValid,
    firstName,
    setFirstName,
    isFirstNameValid,
    setFirstNameValid,
    lastName,
    setLastName,
    isLastNameValid,
    setLastNameValid,
    businessName,
    setBusinessName,
    isBusinessNameValid,
    setBusinessNameValid,
    address,
    setAddress,
    isAddressValid,
    setAddressValid,
    address2,
    setAddress2,
    isAddress2Valid,
    setAddress2Valid,
    zipCode,
    setZipCode,
    isZipCodeValid,
    setZipCodeValid,
    city,
    setCity,
    isCityValid,
    setCityValid,
    country,
    setCountry,
    isCountryValid,
    setCountryValid,
    phone,
    setPhone,
    isPhoneValid,
    setPhoneValid,
    orgNumber,
    setOrgNumber,
    isOrgNumberValid,
    setOrgNumberValid,
    state,
    setState,
    isStateValid,
    setStateValid,
    validForm,
    setValidForm,
    altEmail,
    isAltEmailValid,
    setAltEmail,
    setAltEmailValid,
  } = useValid();

  const getMicrosoftFields = () => ({
    vendorRequiredFields: {
      organizationRegistrationNumber: [orgNumber || ''],
      subDomain: [tenantName || ''],
      userPrincipleName: [principleName || ''],
      firstName: [firstName || ''],
      lastName: [lastName || ''],
      addressLine1: [address || ''],
      addressLine2: [address2 || ''],
      addressCity: [city || ''],
      addressState: [state || ''],
      addressPostalCode: [zipCode || ''],
      alternateEmail: [altEmail || ''],
      email: [email || ''],
      addressPhoneNumber: [phone || ''],
      addressISO3Country: [country || ''],
    },
  });

  const hasMicrosoftProduct = userBasket.items.some(item => item.msftProduct === true);
  let firstMicrosoftProduct: UserBasketItem | undefined;
  if (hasMicrosoftProduct) {
    firstMicrosoftProduct = userBasket.items.find(item => item.msftProduct === true);
  }

  const req_body: ProductOrderBasketProduct = {
    productUuid: firstMicrosoftProduct?.id || '',
    qty: firstMicrosoftProduct?.quantity || 1,
    unit: firstMicrosoftProduct?.dxlInformation?.price[0].unit || 'USER',
    vendorRequiredFields: {
      organizationRegistrationNumber: [orgNumber],
      subDomain: [tenantName],
      userPrincipleName: [principleName],
      firstName: [firstName],
      lastName: [lastName],
      addressLine1: [address],
      addressLine2: [address2],
      addressCity: [city],
      addressState: [state],
      addressPostalCode: [zipCode],
      alternateEmail: [email],
      addressPhoneNumber: [phone],
      addressISO3Country: [country],
    },
  };
  const req_body_keys = Object.keys(req_body.vendorRequiredFields);

  const postProductOrder = async (e: { preventDefault: () => void }) => {
    window.utag?.view({
      event_name: 'ui_interaction',
      component_name: 'Click component',
      event_category: 'Basket',
      event_label: 'complete purchase',
      event_action: 'Basket',
      component_type: 'Button',
    });

    if (currentStep === 2) {
      handleContinueClick();
      return;
    }
    e.preventDefault();
    toggleModal(true, 'LoadingCheckout');

    if (
      user?.organizationId &&
      user?.userId &&
      userBasket &&
      userBasket.items.length > 0 &&
      !userBasket.items.find(item => !item.dxlInformation?.periods || !item.dxlInformation?.periods[0].uuid)
    ) {
      const products = userBasket.items.flatMap(item => {
        const { quantity } = item;

        const isMicrosoftProduct = item.msftProduct;
        
        // Get needsAdditionalInformation for this specific item from basketSummary
        const itemNeedsAdditionalInfo = basketSummary?.data.item?.find(
          (basketItem: any) => basketItem.productId === item.productId
        )?.needsAdditionalInformation;

        // For bundled products, include the free items as well
        if (
          item.__typename === PRODUCT_BUNDLE_TYPENAME &&
          item.productsFreeOfCharge &&
          item.productsFreeOfCharge.length > 0
        ) {
          return [
            {
              ...transformToItemOrder(item, undefined),
              ...(isMicrosoftProduct && itemNeedsAdditionalInfo ? getMicrosoftFields() : {}),
            },
            ...item.productsFreeOfCharge.map(subItem => ({
              ...transformToItemOrder(subItem, quantity),
              ...(isMicrosoftProduct && itemNeedsAdditionalInfo ? getMicrosoftFields() : {}),
            })),
          ];
        } else if (item.__typename === PRODUCT_BUNDLE_TYPENAME && !item.productsFreeOfCharge) {
          return [
            {
              ...transformToItemOrder(item, undefined),
              ...(isMicrosoftProduct && itemNeedsAdditionalInfo ? getMicrosoftFields() : {}),
            },
          ];
        }

        // Regular products
        return [
          {
            ...transformToItemOrder(item, undefined),
            ...(isMicrosoftProduct && itemNeedsAdditionalInfo ? getMicrosoftFields() : {}),
          },
        ];
      }) as unknown as ProductOrderBasketProduct[];

      //grab tenant porting value, if true send x-is-tenant-porting header
      //making the api call for validation
      const res = await productOrder(
        {
          basket: {
            organizationId: user?.organizationId,
            userId: user?.userId,
            // INFO: The order sent to the marketplace need to be checked if it's being calculated right, because here
            // we are just sending the prices to cost one, maybe we should send every unit price related to the product.
            products,
          },
        },
        user.csrfToken || '',
        setErrorInfo,
        toggleModal,
        tenantPortingRequired
      );
      toggleModal(false, 'LoadingCheckout');

      // for creditvetting validation
      if (res && !axios.isAxiosError(res) && res?.data?.needCreditVetting) {
        setDisableContinueButton(true);
        setCurrentStep(2); // go to review ( CV ) page
      } else if (res && !axios.isAxiosError(res) && res?.data?.externalId) {  
        
        // for successful purchase
        dispatch(deleteBasket());
        return usingOpcoUrl(
          `/new-order-flow-success-page?id=${res.data.externalId}&email=${res.data.userEmail}`,
          true
        );
      }
    }
  };

  const handleCreditVettingSubmit = async (payload: CreditVettingPayload) => {
    const validateFields = (errorMsg: string | null, req_body_keys: string[]) => {
      // Start assuming “invalid”:
      let isFormValid = false;

      if (!errorMsg) {
        // No error message at all → valid
        isFormValid = true;
      } else {
        // 1) Parse out your ValidationItems
        const itemRegex =
          /ValidationItem\(itemId=([a-z0-9-]+),\s*code=([A-Z_]+),\s*message=(.+?)(?=,\s*messageParameters=\[),\s*messageParameters=\[([^\]]+)\]\)/g;
        const paramRegex = /MessageParameter\(key=([^,]+),\s*value=([^)]+)\)/g;

        const validationItems: Array<{
          itemId: string;
          code: string;
          message: string;
          messageParameters: { key: string; value: string }[];
        }> = [];

        let m: RegExpExecArray | null;
        while ((m = itemRegex.exec(errorMsg))) {
          const params: { key: string; value: string }[] = [];
          let pm: RegExpExecArray | null;
          while ((pm = paramRegex.exec(m[4]))) {
            params.push({ key: pm[1].trim(), value: pm[2].trim() });
          }
          validationItems.push({
            itemId: m[1],
            code: m[2],
            message: m[3],
            messageParameters: params,
          });
        }

        if (validationItems.length === 0) {
          // No invalid items extracted → valid
          isFormValid = true;
        } else {
          // 2) Build a map of field‑name → setter
          const setterMap: Record<string, (valid: boolean) => void> = {
            OrganizationRegistrationNumber: setOrgNumberValid,
            SubDomain: setIsTenantValid,
            UserPrincipleName: setIsPrincipleNameValid,
            FirstName: setFirstNameValid,
            LastName: setLastNameValid,
            'Address/Line1': setAddressValid,
            addressLine2: setAddress2Valid,
            'Address/City': setCityValid,
            'Address/State': setStateValid,
            'Address/PostalCode': setZipCodeValid,
            AlternateEmail: setAltEmailValid,
            email: setEmailValid,
            'Address/PhoneNumber': setPhoneValid,
            'Address/ISO3Country': setCountryValid,
          };

          // 3) Determine which fields failed (grab the quoted key from each message)
          const failedKeys = new Set<string>();
          const fieldNameRegex = /'([^']+)'/;

          validationItems.forEach(({ message }) => {
            const execResult = fieldNameRegex.exec(message);
            if (execResult) {
              failedKeys.add(execResult[1]);
            }
          });

          // 4) Apply each setter and track if any failed
          let anyFailed = false;
          Object.entries(setterMap).forEach(([key, setter]) => {
            const valid = !failedKeys.has(key);
            setter(valid);
            if (!valid) anyFailed = true;
          });

          // Overall form is valid only if none failed
          isFormValid = !anyFailed;
        }
      }

      // 5) Update the global form‑valid state
      setValidForm(isFormValid);

      //add tealium event for complete Purchase
      window.utag?.view({
        event_name: 'ui_interaction',
        component_name: 'Click Component',
        event_category: 'Basket',
        event_label: 'complete Purchase',
        event_action: 'Basket',
        component_type: 'Button',
      });

      // 6) Single exit point
      return isFormValid;
    };

    if (payload.bankStartDay == null || payload.businessStartDate == null) {
      return;
    }
    toggleModal(true, 'LoadingCheckout');
    const res = await creditVettingProductOrder(payload, user?.csrfToken || '', setErrorInfo, toggleModal, false);
    toggleModal(false, 'LoadingCheckout');

    if (res && res?.response?.data?.message) {
      // Extract errorMsg and call validateFields
      const errorMsg = res?.response?.data?.message;
      const isValid = validateFields(errorMsg, req_body_keys);
    }

    if (
      res &&
      !(res?.response?.data?.error && typeof res?.response?.data?.error === 'string') &&
      res.name !== 'AxiosError'
    ) {
      // for successful purchase after credit vetting
      dispatch(deleteBasket());
      return usingOpcoUrl(
        `/new-order-flow-success-page?id=${res.data.externalId}&email=${res.data.userEmail}`,
        true
      );
    } else {
      const errorCode = res?.response?.data?.errorCode;
      const isCreditVettingError = errorCode.includes('CREDIT_VETTING') || '';
      if (isCreditVettingError) {
        setInlineNotificationVisible(true);
      } else {
        setCurrentStep(1);
      }
    }
  };

  const handleCreditVettingChange = (payload: CreditVettingPayload) => {
    if (
      payload.bankStartDay == null ||
      payload.bankStartDay == '' ||
      payload.businessStartDate == null ||
      payload.businessStartDate == ''
    ) {
      setDisableContinueButton(true);
    } else {
      setDisableContinueButton(false);
    }
  };

  const { discountData } = useAppSelector(state => state.userDiscount);

  const handleSetDisableContinueButton = useCallback((value: boolean | ((prevState: boolean) => boolean)) => {
    setDisableContinueButton(value);
  }, []);

  useEffect(() => {
    // Only save to sessionStorage if it doesn't already exist

    const originalPrices = userBasket.items.reduce((acc, item) => {
      acc[item.productId] = item.dxlInformation?.price[0].price;
      return acc;
    }, {});

    sessionStorage.setItem('originalBasketPrices', JSON.stringify(originalPrices));
  }, []);

  useEffect(() => {
    if (userBasket.items.length > 0) {
      fetchBasketSummary(userBasket, user);
    }
  }, []);

  const BasketContent = () => {
    return (
      <SWBasketSummaryInner>
        <Heading level={2} size={2} weight={3} text={yourOrderSummary} />
        {userBasket.items.map(product => {
          if (product.__typename === PRODUCT_BUNDLE_TYPENAME) {
            const discountValue = discountData?.discountValue;
            const productDiscount = discountData?.itemDiscount?.find(item => item.productId === product.productId);
            const hasDiscount =
              (productDiscount?.productRecurringDiscount && productDiscount.productRecurringDiscount > 0) ||
              (productDiscount?.productUpfrontDiscount && productDiscount.productUpfrontDiscount > 0);
            const discountObj = useMemo(
              () => ({
                discountData: hasDiscount ? discountValue : undefined,
              }),
              [hasDiscount, discountValue]
            );

            return (
              <>
                <BasketItem
                  key={product.id}
                  {...product}
                  discountData={discountObj}
                  continueBtn={handleSetDisableContinueButton}
                  microsoftItemState={setMicrosoftAdditionalData}
                  microsoftState={microsoftAdditionalData}
                  needsMCA={needsMCA}
                  isSummaryLoaded={isSummaryLoaded}
                />
                {product?.productsFreeOfCharge?.map(productFreeOfCharge => (
                  <BasketItem
                    key={productFreeOfCharge.id}
                    {...productFreeOfCharge}
                    increaseDecreaseAct={false}
                    quantity={product.quantity}
                  />
                ))}
              </>
            );
          }
        })}
      </SWBasketSummaryInner>
    );
  };

  return (
    <SWBasketStyles>
      <SWBasketContainer {...ContainerHeaderStyles(width)}>
        <SWBasketHeader>
          <Heading level={3} justify={'center'} text={name} />
          <Paragraph size={2}>
            <span>{accountTrans}</span>
            <br />
            <b>{user?.selectedAccount?.customerAccountId}</b>
          </Paragraph>
        </SWBasketHeader>
      </SWBasketContainer>
      {userBasket?.items.length > 0 ? (
        <>
          <BasketHorizontalStepper currentStep={currentStep} />
          <SWBasketContainer backgroundColor="#F4F4F4">
            <SWBasketSummary>
              {currentStep === 1 && <BasketContent />}
              {currentStep === 2 && (
                <CreditVettingForm
                  ref={creditVettingFormRef}
                  description="Please validate bank registration details before proceeding to the next step"
                  __typename="ContentfulSectionCreditVettingForm"
                  id="credit-vetting-form-basket"
                  onSubmit={handleCreditVettingSubmit}
                  onChange={handleCreditVettingChange}
                  inlineNotificationVisible={inlineNotificationVisible}
                  inlineNotificationMessage={cvInlineNotificationMessage}
                />
              )}
            </SWBasketSummary>
            {basketSummary ? (
              <BasketDiscount shouldShowDiscountInput={currentStep === 1} basketSummary={basketSummary.data} />
            ) : (
               <LoadingWrapper>
                        <LoadingSpinner appearance="primary" size={5} />
                      </LoadingWrapper>
            )}
          </SWBasketContainer>

          <BasketStrip
            continueShoppingButton={continueShoppingButton}
            requestCallbackButton={requestCallbackButton}
            goToCheckoutButton={goToCheckoutButton}
            postProductOrder={postProductOrder}
            disableContinueButton={disableContinueButton}
            hideContinueShoppingButton={currentStep === 2}
          />
        </>
      ) : (
        <SWBasketContainer backgroundColor="#F4F4F4">
          <SWBasketSummary>
            <EmptyBasket>
              <Heading size={2} level={3} weight={3}>
                {emptyBasket}
              </Heading>
              <Paragraph weight={1}>{emptyBasketSubtitle}</Paragraph>
              <WrapperBasketButtons>
                <BasketButtonsInner>
                  {OPCO !== OPCOOptions.PORTUGAL && (
                    <Button
                      text={continueShoppingButton.label}
                      appearance="primary"
                      href={continueShoppingButton.url}
                      data-selector="MS365-BundleConfig-OrderSummary-Next"
                    />
                  )}
                  {requestCallbackButton && (
                    <Button
                      text={requestCallbackButton.label}
                      appearance="alt1"
                      href={requestCallbackButton?.url}
                      data-selector="MS365-BundleConfig-OrderSummary-RequestCallback"
                    />
                  )}
                </BasketButtonsInner>
              </WrapperBasketButtons>
            </EmptyBasket>
          </SWBasketSummary>
        </SWBasketContainer>
      )}
    </SWBasketStyles>
  );
};

export default SWBasket;
